import { useState, useCallback, useMemo, ChangeEvent } from 'react'
import { assoc, test, replace, trim } from 'ramda'

import { type ImageParamsType } from 'app/common/components/ImagePrepare'
import { priceUnsignedRe } from 'app/common/constants/regex'
import { api } from 'app/api'
import {
  POST_FOOD_ITEMS,
  convertResult,
  convertParams,
} from 'app/common/api/requests/food/items/post'
import usePopup from 'app/common/hooks/usePopup'

type FormState = {
  nomenclature: string
  title: string
  photo: ImageParamsType | null
  portion: string
  cost: string
  saleType: string
  addDishToOrgs: boolean
  submitting: boolean
  errorText: string
}

const defaultFormState: FormState = {
  nomenclature: '',
  title: '',
  photo: null,
  portion: '',
  cost: '',
  saleType: '',
  addDishToOrgs: false,
  submitting: false,
  errorText: '',
}

export type FormErrorState = {
  nomenclature: string
  title: string
  cost: string
  saleType: string
  addDishToOrgs: string
}

const defaultErrorState: FormErrorState = {
  nomenclature: '',
  title: '',
  cost: '',
  saleType: '',
  addDishToOrgs: '',
}

type BindData = {
  nomenclature: {
    value: string
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    isError: boolean
  }
  title: {
    value: string
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    isError: boolean
  }
  photo: {
    photo: ImageParamsType | null
    filenameLocal: string
    onSubmit: (photo: ImageParamsType, filename: string) => boolean
  }
  portion: {
    value: string
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  }
  cost: {
    value: string
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    isError: boolean
  }
  saleType: {
    value: string
    onChange: (itemProps: { id: string }) => void
    errored: boolean
  }
  addDishToOrgs: {
    value: boolean
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    isError: boolean
  }
  submitting: boolean
  errors: FormErrorState
}

type ApiHandlers = {
  submit: () => boolean | Promise<boolean>
}

type Options = {
  onChangeNomenclature: (s: string) => void
  onChangeTitle: (s: string) => void
  onChangePortion: (s: string) => void
  onChangeCost: (s: string) => void
  nomenclatureActive: boolean
}

export default function useAddingForm({
  onChangeNomenclature,
  onChangeTitle,
  onChangePortion,
  onChangeCost,
  nomenclatureActive,
}: Options): [BindData, ApiHandlers] {
  const [fields, setFields] = useState(defaultFormState)
  const [filenameLocal, setFilenameLocal] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState(defaultErrorState)
  const popup = usePopup()

  const handleChangeNomenclature = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      setFields(assoc('nomenclature', value))
      onChangeNomenclature(value)
      setErrors(assoc('nomenclature', defaultErrorState.nomenclature))
    },
    [onChangeNomenclature],
  )

  const handleChangeTitle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      setFields(assoc('title', value))
      onChangeTitle(value)
      setErrors(assoc('title', defaultErrorState.title))
    },
    [onChangeTitle],
  )

  const handleChangePhoto = useCallback((photo: ImageParamsType, filename: string) => {
    setFilenameLocal(filename)
    setFields(assoc('photo', photo))
    return true
  }, [])

  const handleChangePortion = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      setFields(assoc('portion', value))
      onChangePortion(value)
    },
    [onChangePortion],
  )

  const handleChangeCost = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      const replaced = replace(',', '.', value)
      if (test(priceUnsignedRe, replaced)) {
        setFields(assoc('cost', replaced))
        onChangeCost(replaced)
        setErrors(assoc('cost', defaultErrorState.cost))
      }
    },
    [onChangeCost],
  )

  const handleChangeSaleType = useCallback((itemProps: { id: string }) => {
    const { id } = itemProps
    setFields(assoc('saleType', id))
    setErrors(assoc('saleType', defaultErrorState.saleType))
  }, [])

  const handleChangeAddDishToOrgs = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.currentTarget
    setFields(assoc('addDishToOrgs', checked))
    setErrors(assoc('addDishToOrgs', defaultErrorState.addDishToOrgs))
  }, [])

  const handleCheckErrors = useCallback(() => {
    let success = true
    if (nomenclatureActive && !trim(fields.nomenclature)) {
      setErrors(assoc('nomenclature', 'Необходимо указать номенклатуру'))
      success = false
    }
    if (!trim(fields.title)) {
      setErrors(assoc('title', 'Необходимо указать наименование'))
      success = false
    }
    if (!trim(fields.cost)) {
      setErrors(assoc('cost', 'Необходимо указать стоимость'))
      success = false
    }
    if (!trim(fields.saleType)) {
      setErrors(assoc('saleType', 'Необходимо указать тип продукции'))
      success = false
    }
    return success
  }, [fields.title, fields.cost, fields.saleType, fields.nomenclature, nomenclatureActive])

  const handleSubmit = useCallback(async () => {
    if (handleCheckErrors()) {
      setSubmitting(true)
      const res = await api.request(POST_FOOD_ITEMS, {
        error: 'Не удалось создать продукцию',
        convertions: {
          convertParams,
          convertResult,
        },
        params: {
          title: fields.title,
          portion: fields.portion,
          cost: fields.cost,
          saleTypeId: fields.saleType,
          addDishToOrgs: fields.addDishToOrgs,
          ...(fields.photo ? { photo: fields.photo } : {}),
          ...(nomenclatureActive ? { nomenclature: fields.nomenclature } : {}),
        },
      })
      setSubmitting(false)

      if (res) {
        popup('Продукция успешно создана')
        setFields((fields) => ({ ...defaultFormState, addDishToOrgs: fields.addDishToOrgs }))
        setFilenameLocal('')
        return true
      }
    }
    return false
  }, [handleCheckErrors, fields, popup, nomenclatureActive])

  const nomenclature = useMemo(
    () => ({
      value: fields.nomenclature,
      onChange: handleChangeNomenclature,
      isError: Boolean(errors.nomenclature),
    }),
    [fields.nomenclature, errors.nomenclature, handleChangeNomenclature],
  )

  const title = useMemo(
    () => ({
      value: fields.title,
      onChange: handleChangeTitle,
      isError: Boolean(errors.title),
    }),
    [fields.title, errors.title, handleChangeTitle],
  )

  const photo = useMemo(
    () => ({
      photo: fields.photo,
      filenameLocal,
      onSubmit: handleChangePhoto,
    }),
    [fields.photo, filenameLocal, handleChangePhoto],
  )

  const portion = useMemo(
    () => ({
      value: fields.portion,
      onChange: handleChangePortion,
    }),
    [fields.portion, handleChangePortion],
  )

  const cost = useMemo(
    () => ({
      value: fields.cost,
      onChange: handleChangeCost,
      isError: Boolean(errors.cost),
    }),
    [fields.cost, errors.cost, handleChangeCost],
  )

  const saleType = useMemo(
    () => ({
      value: fields.saleType,
      onChange: handleChangeSaleType,
      errored: Boolean(errors.saleType),
    }),
    [fields.saleType, errors.saleType, handleChangeSaleType],
  )

  const addDishToOrgs = useMemo(
    () => ({
      value: fields.addDishToOrgs,
      onChange: handleChangeAddDishToOrgs,
      isError: Boolean(errors.addDishToOrgs),
    }),
    [fields.addDishToOrgs, errors.addDishToOrgs, handleChangeAddDishToOrgs],
  )

  return [
    {
      nomenclature,
      title,
      photo,
      portion,
      cost,
      saleType,
      addDishToOrgs,
      submitting,
      errors,
    },
    {
      submit: handleSubmit,
    },
  ]
}
